import React, {useCallback, useMemo} from 'react'
import Skeleton from 'react-loading-skeleton'
import styled from 'styled-components'
import dayjs from 'dayjs'
import {
  ButtonIcon,
  ButtonOutline,
  Icon,
  Image,
  Paragraph,
  ParsedText,
} from 'common/components'
import {useTranslation} from 'i18n'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import {useTheme} from 'themes'
import {parseShape} from 'types'
import {
  getBoxShadow,
  getDefaultAvatar,
  getFontFamily,
  parseDate,
  showModalLimitedAccess,
  showModalReprocessKyc,
  useHistory,
} from 'utils'
import {FototreeMapCreatorPopupContentProps} from './FototreeMapCreatorProps'

const StyledImage = styled(Image)`
  width: ${convertUnit(68)};
  aspect-ratio: 0.75;
  border-radius: ${convertUnit(8)};
  object-fit: contain;
`

const StyledContainer = styled.div`
  display: flex;
  gap: ${convertUnit(20)};
  padding: ${convertUnit(20)};
  box-shadow: ${({theme}) =>
    getBoxShadow(theme, {verticalOffset: 4, opacity: 0.16})};
  border-radius: ${convertUnit(16)};
  background-color: ${({theme}) => theme.white_1};
`

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledScheduleContainer = styled.div`
  display: inline-flex;
`

const StyledButtonWrapper = styled.div`
  display: flex;
  margin-top: ${convertUnit(8)};
  gap: ${convertUnit(8)};
`

export default function FototreeMapCreatorPopupContent({
  data,
  onUpdateReducer,
}: FototreeMapCreatorPopupContentProps) {
  const history = useHistory()
  const theme = useTheme()
  const {translate} = useTranslation()
  const {id} = useSelector('user') || {}
  const {kycStatus} = useSelector('yuserActivationState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const {status: childStatus} = useSelector('parentalConsentState')

  const handleFeatureDisabled = useCallback(() => {
    if (childStatus) {
      showModalLimitedAccess()
      return
    }
    if (kycStatus === 'onhold') {
      if (similarStatus === 'CHOOSE_SELF') {
        return showModalReprocessKyc()
      }
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [childStatus, history, kycStatus, similarStatus])

  const handleClickProfile = useCallback(() => {
    onUpdateReducer()
    if (childStatus) {
      showModalLimitedAccess()
      return
    }
    if (!data) return
    history.push(
      'giftshop_profile',
      {
        self: data.member_id === id,
        postData: {
          member_id: data.member_id,
          username: data.member_username,
          photo: data.member_url,
        },
      },
      data.member_username,
    )
  }, [childStatus, data, history, id, onUpdateReducer])

  const handleClickYourPhotos = useCallback(() => {
    const {member_url, creator_id, member_username} = data || {}
    if (!creator_id || !member_url || !member_username) return
    onUpdateReducer()
    history.pushQuery({
      path: 'giftshop_search_by_creator_creation_content',
      state: {
        photo: member_url,
      },
      queryObject: {
        creator_id,
        username: member_username,
      },
    })
  }, [data, history, onUpdateReducer])

  const handleClickChat = useCallback(() => {
    onUpdateReducer()
    if (
      childStatus ||
      !(
        kycStatus === 'verified' ||
        kycStatus === 'verified_check' ||
        kycStatus === 'onhold'
      )
    ) {
      handleFeatureDisabled()
      return
    }
    if (!data) return
    history.push('giftshop_message_room', {
      participantId: data.member_id,
      roomType: 'chat',
      roomName: data.member_username,
      roomPhoto: data.member_url,
    })
  }, [
    childStatus,
    data,
    handleFeatureDisabled,
    history,
    kycStatus,
    onUpdateReducer,
  ])

  const patterns: parseShape[] = useMemo(
    () => [
      {
        pattern: /<date>.*?<\/date>/,
        style: {
          color: theme.black_1,
          fontFamily: getFontFamily('bold'),
        },
        renderText: (text) => text.replace(/<date>|<\/date>/g, ''),
      },
    ],
    [theme.black_1],
  )

  const relativeDate = useMemo(() => {
    if (!data?.expired_at) return

    const date = new Date(data.expired_at)
    if (dayjs().isSame(date, 'day')) {
      return `${translate('global:today')}, ${parseDate(
        data.expired_at,
        'HH:mm',
      )}`
    }
    if (dayjs().add(1, 'day').isSame(date, 'day')) {
      return `${translate('global:tomorrow')}, ${parseDate(
        data.expired_at,
        'HH:mm',
      )}`
    }
    return parseDate(data.expired_at, 'D MMMM YYYY, HH:mm')
  }, [data?.expired_at, translate])

  return (
    <StyledContainer>
      <StyledImage
        src={data?.member_url || getDefaultAvatar()}
        alt={data?.member_username || 'Profile Picture'}
      />
      <StyledContentContainer>
        <Paragraph fontWeight="bold" fontSize="l" lineHeight={31}>
          {data?.member_username ? (
            data.member_username
          ) : (
            <Skeleton
              borderRadius={17}
              width={70}
              baseColor={theme.gray_1}
              highlightColor={theme.white_4}
            />
          )}
        </Paragraph>
        {data?.expired_at ? (
          <StyledScheduleContainer>
            <Icon type="calendar-event" size={convertUnit(16)} color="gray_5" />
            <Paragraph>
              <ParsedText parse={patterns}>
                {translate('tree:creatorViewCameraLiveAction', {
                  date: relativeDate,
                })}
              </ParsedText>
            </Paragraph>
          </StyledScheduleContainer>
        ) : (
          <Skeleton
            borderRadius={17}
            baseColor={theme.gray_1}
            highlightColor={theme.white_4}
          />
        )}
        <StyledButtonWrapper>
          <ButtonIcon
            type="smallest"
            iconColor={!data?.member_username ? 'gray_3' : 'white_1'}
            iconType="account"
            hasShadow={false}
            disabled={!data?.member_username}
            onClick={handleClickProfile}
          />
          <ButtonIcon
            rounded
            type="smallest"
            iconColor={!data?.member_username ? 'gray_3' : 'white_1'}
            iconType="message"
            hasShadow={false}
            disabled={!data?.member_username}
            onClick={handleClickChat}
          />
          <ButtonOutline
            color={!data?.member_username ? 'gray_3' : 'gray_5'}
            type="smallest"
            fontSize="s"
            label={translate('giftShop:yourPhotos')}
            width="100%"
            leftIcon={
              <Icon
                type="search"
                color={!data?.member_username ? 'gray_3' : 'gray_5'}
                size={convertUnit(16)}
                marginRight={convertUnit(4)}
              />
            }
            disabled={!data?.member_username}
            onClick={handleClickYourPhotos}
          />
        </StyledButtonWrapper>
      </StyledContentContainer>
    </StyledContainer>
  )
}
